import { useState, useEffect } from 'react';
import axios from 'axios';
import { ExternalLink, Filter, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const NewsTab = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      console.log('📰 Fetching crypto news from:', `${API}/api/crypto-news`);
      const response = await axios.get(`${API}/api/crypto-news?limit=30`);
      console.log('📰 News response:', response.data);
      console.log('📰 Articles count:', response.data?.articles?.length);
      setNews(response.data.articles || []);
    } catch (error) {
      console.error('❌ Error fetching news:', error);
      toast.error('Failed to load news: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (publishedAt) => {
    try {
      const date = new Date(publishedAt);
      const now = new Date();
      const seconds = Math.floor((now - date) / 1000);
      
      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
      return `${Math.floor(seconds / 86400)} days ago`;
    } catch {
      return 'Recently';
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="glass-card p-4 rounded-lg border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#00FF94]" />
            <h2 className="text-xl font-bold">Latest Crypto News</h2>
          </div>
          <span className="text-sm text-muted-foreground">Updates every 5 minutes</span>
        </div>
      </div>

      {/* News Feed */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading latest crypto news...</p>
        </div>
      ) : news.length === 0 ? (
        <div className="glass-card p-12 rounded-lg border-white/10 text-center">
          <p className="text-muted-foreground">No news available at the moment.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {news.map((article, index) => (
            <div
              key={article.id || index}
              className="glass-card p-5 rounded-lg border-white/10 hover:border-primary/50 transition-all cursor-pointer group"
              onClick={() => window.open(article.url, '_blank')}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors">
                      {article.title}
                    </h3>
                    <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0 group-hover:text-primary transition-colors" />
                  </div>
                  
                  {article.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {article.description}
                    </p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <TrendingUp className="w-3 h-3" />
                      {article.source}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {getTimeAgo(article.published_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsTab;